import { IonIcon, IonSkeletonText, IonSpinner } from "@ionic/react";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, PaymentStatusType } from "@core/types";
import { useGetCardsQuery } from "../../../providers/cards";
import { AppLauncher } from "@capacitor/app-launcher";
import { addOutline, radioButtonOffOutline, radioButtonOnOutline } from "ionicons/icons";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/auth-context";
import {
  useCreateYooKassaPaymentMutation,
  usePayWithSavedMethodMutation,
  useGetPaymentStatus,
} from "../../../providers/payment";
import { BottomSheet as SpringBottomSheet } from "react-spring-bottom-sheet";

import PaymentCardLogo from "../../../parhato2/src/PaymentCardLogo";
import { useGetOrderByIdQuery } from "../../../providers/Orders";
import { calculateTotalAppliedCouponsAndRewardPoints } from "../../../utils/calculateTotalAppliedCoupons";
import { useSbpPaymentMutation } from "../../../providers/payment/yookassa/useSbpPayment";
import sbp from "../../../parhato2/src/shared/assets/icons/sbp.png";

interface PaymentBottomSheetProps {
  orderId: string;
  onClose: () => void;
  onSuccess: () => void;
  showPaymentForm: boolean;
}

type PaymentMethodType = { type: "CARD"; cardId: string } | { type: "SBP" } | { type: "ADD_NEW" };

const cardsSkeleton = [...new Array(3).keys()];

const PaymentBottomSheet = ({ orderId, onClose, showPaymentForm, onSuccess }: PaymentBottomSheetProps) => {
  const { user } = useAuth();
  const userId = user?.id;

  const { t } = useTranslation();
  // Флаг для отмены платежного процесса
  const paymentCancelledRef = useRef(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusType | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>({ type: "SBP" });

  const handleSuccessPayment = () => {
    onSuccess();
    setPaymentId(null);
    setPaymentStatus(null);
    setIsPaymentLoading(false);
  };

  useGetPaymentStatus(paymentId, paymentStatus, orderId, setPaymentStatus, handleSuccessPayment);

  const { data: cards, isLoading: isCardsLoading } = useGetCardsQuery();
  const { mutateAsync: createSbpPaymentAsync, isPending: isCreateSbpPaymentPending } = useSbpPaymentMutation();
  const { mutateAsync: createYooKassaPaymentAsync, isPending: isCreateYooKassaPaymentPending } =
    useCreateYooKassaPaymentMutation();
  const { mutateAsync: payWithSavedMethodMutationAsync, isPending: isPayWithSavedMethodPending } =
    usePayWithSavedMethodMutation();

  const {
    data: order,
    isLoading: isOrderLoading,
    isError: isOrderError,
  } = useGetOrderByIdQuery(orderId, {
    enabled: !!orderId,
    refetchOnMount: "always",
    refetchInterval: 60000,
  });

  const price = order ? calculateTotalAppliedCouponsAndRewardPoints(order) : 0;

  const isLoading =
    paymentStatus === "pending" ||
    paymentStatus === "succeeded" ||
    isPaymentLoading ||
    isOrderLoading ||
    isCreateSbpPaymentPending ||
    isCreateYooKassaPaymentPending ||
    isPayWithSavedMethodPending;

  const selectedCard =
    selectedPaymentMethod?.type === "CARD" ? cards?.find((c) => c.id === selectedPaymentMethod.cardId) || null : null;

  // Функция отмены платежа
  const handleCancelPayment = () => {
    paymentCancelledRef.current = true; // Устанавливаем флаг отмены
    setIsPaymentLoading(false);
    setPaymentStatus(null);
    setPaymentId(null);
    toast.warning(t("Payment was cancelled"), {
      toastId: "cancel-payment",
      autoClose: 3000,
    });
    toast.info(t("Please try again if needed"), {
      toastId: "cancel-payment-info",
      autoClose: 3000,
    });
  };

  const handleSelectPaymentMethod = (method: PaymentMethodType) => {
    setSelectedPaymentMethod(method);
  };

  const handlePay = async (): Promise<void> => {
    // Если платеж уже прошел или заказ не требует оплаты, выходим
    if (paymentStatus === "succeeded" || order?.needsPayment === false) {
      return;
    }

    // Если пользователь не авторизован завершаем процесс
    if (!userId) {
      toast.warn(`${t("Login Required")}: ${t("Please log in to create an order.")}`);
      return;
    }

    // Если цена невалидна завершаем процесс
    if (!price || price <= 0) {
      toast.warn(`${t("Invalid Price")}: ${t("Invalid price. Please try again.")}`);
      return;
    }

    // Если метод оплаты не выбран завершаем процесс
    if (!selectedPaymentMethod) {
      toast.warn(t("Please select a payment method"));
      return;
    }

    // Сбрасываем флаг отмены платежа, чтобы можно было повторить платеж
    paymentCancelledRef.current = false;

    // Устанавливаем флаг загрузки
    setIsPaymentLoading(true);

    // Устанавливаем статус ожидания
    setPaymentStatus("pending");

    try {
      // Обработка платежа через SBP
      if (selectedPaymentMethod.type === "SBP") {
        return await processSbpPayment();
      }

      // Обработка платежа через сохраненную карту
      if (selectedPaymentMethod.type === "CARD" && selectedCard) {
        return await processCardPayment(selectedCard);
      }

      // Обработка платежа через новую карту
      if (selectedPaymentMethod.type === "ADD_NEW") {
        return await processNewCardPayment();
      }

      throw new Error(t("Invalid payment method"));
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(`${t("Error processing payment")}: ${error?.message || t("Unknown error")}`);
      setPaymentStatus("failed");
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const processSbpPayment = async (): Promise<void> => {
    const res = await createSbpPaymentAsync({ customerId: userId!, orderId: orderId });

    if (!res) {
      throw new Error(t("Error processing payment"));
    }

    setPaymentId(res.id.toString() ?? null);

    if (res.confirmationUrl) {
      if (paymentCancelledRef.current) return;
      await AppLauncher.openUrl({ url: res.confirmationUrl });
    } else {
      throw new Error(t("Error processing payment"));
    }
  };

  const processCardPayment = async (card: Card): Promise<void> => {
    const { data: responseData, error: payWithSavedMethodError }: any = await payWithSavedMethodMutationAsync({
      amount: {
        value: price.toString(),
        currency: "RUB",
      },
      payment_method_id: card.paymentMethodId,
      metadata: {
        profile_id: userId!,
        order_id: orderId,
      },
    });

    if (payWithSavedMethodError || !responseData) {
      throw new Error(payWithSavedMethodError?.message || t("Error processing payment"));
    }

    setPaymentId(responseData?.metadata?.payment_id);

    const confirmationUrl = responseData?.confirmation?.confirmation_url;

    if (confirmationUrl) {
      if (paymentCancelledRef.current) {
        console.log("Payment was cancelled before opening browser");
        return;
      }
      await AppLauncher.openUrl({ url: confirmationUrl });
    } else if (responseData.status === "canceled") {
      const cancellationReason = responseData?.cancellation_details?.reason;
      throw new Error(t(cancellationReason));
    }
  };

  const processNewCardPayment = async (): Promise<void> => {
    const { data: createYooKassaPaymentResponse, error: createYooKassaPaymentError }: any =
      await createYooKassaPaymentAsync({
        amount: price,
        profileId: userId!,
        orderId: orderId,
      });

    if (createYooKassaPaymentError || !createYooKassaPaymentResponse) {
      throw new Error(createYooKassaPaymentError?.message || t("Unknown error"));
    }

    setPaymentId(createYooKassaPaymentResponse?.metadata?.payment_id);

    if (createYooKassaPaymentResponse?.confirmation?.confirmation_token) {
      const paymentUrlParams = new URLSearchParams({
        confirmationToken: createYooKassaPaymentResponse.confirmation.confirmation_token,
        orderId: orderId,
      });

      if (createYooKassaPaymentResponse?.metadata?.payment_id) {
        paymentUrlParams.set("paymentId", createYooKassaPaymentResponse.metadata.payment_id);
      }

      const paymentUrl = `${process.env.REACT_APP_PUBLIC_PAYMENTS_URL}/?${paymentUrlParams.toString()}`;

      // Проверяем перед открытием браузера
      if (paymentCancelledRef.current) {
        console.log("Payment was cancelled before opening browser");
        return;
      }
      await AppLauncher.openUrl({ url: paymentUrl });
    } else {
      throw new Error(t("Payment confirmation token not received"));
    }
  };

  return (
    <SpringBottomSheet
      open={showPaymentForm}
      snapPoints={({ maxHeight }) => [maxHeight * 0.5, maxHeight * 0.75, maxHeight * 0.9]}
      onDismiss={onClose}
      header={
        !isOrderError && (
          <div className="text-center text-lg font-bold">
            {t("To pay")} {price.toLocaleString("ru-RU")} {order?.currencySymbol}
          </div>
        )
      }
      footer={
        !isOrderError && (
          <div className="flex flex-col gap-2 w-full">
            {isLoading ? (
              <>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <IonSpinner name="crescent" />
                  <span>{t("Please wait...")}</span>
                </div>
                <button className="w-full px-24 py-3 rounded-3xl bg-red-400 text-white" onClick={handleCancelPayment}>
                  {t("Cancel Payment")}
                </button>
              </>
            ) : (
              <button
                className="w-full px-24 py-3 rounded-3xl bg-green-400 text-white disabled:opacity-50"
                onClick={handlePay}
                disabled={!selectedPaymentMethod}
              >
                {t("Pay Now")}
              </button>
            )}
          </div>
        )
      }
    >
      {isOrderError ? (
        <div className="flex flex-col gap-4 m-4">
          <div className="text-center text-red-600">{t("Error loading order")}</div>
        </div>
      ) : (
        <div className="flex flex-col m-4 gap-2">
          {isCardsLoading ? (
            <div className="flex flex-col gap-4">
              {cardsSkeleton.map((card) => (
                <div key={card} className="flex items-center gap-2.5">
                  <IonSkeletonText className="h-8 w-12 rounded-xl m-0" animated />
                  <IonSkeletonText className="h-6 w-36 rounded-xl m-0" animated />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <button
                className="flex justify-between items-center gap-4 cursor-pointer disabled:opacity-50"
                disabled={isLoading}
                onClick={() => handleSelectPaymentMethod({ type: "SBP" })}
              >
                <div className="flex items-center gap-2">
                  <img src={sbp} alt="SBP" className="w-24 h-auto" />
                  <div className="font-medium">{t("Pay via SBP")}</div>
                </div>
                {selectedPaymentMethod?.type === "SBP" ? (
                  <IonIcon icon={radioButtonOnOutline} color="success" />
                ) : (
                  <IonIcon icon={radioButtonOffOutline} color="gray" />
                )}
              </button>

              <div className="text-center font-medium text-gray-600">или</div>

              {cards?.map((card) => {
                const isSelected = selectedPaymentMethod?.type === "CARD" && selectedPaymentMethod.cardId === card.id;

                return (
                  <button
                    key={card.id}
                    disabled={isLoading}
                    className="flex justify-between items-center gap-4 cursor-pointer disabled:opacity-50"
                    onClick={() => handleSelectPaymentMethod({ type: "CARD", cardId: card.id })}
                  >
                    <PaymentCardLogo cardType={card.cardType} cardLastFour={card.cardLastFour} />
                    {isSelected ? (
                      <IonIcon icon={radioButtonOnOutline} color="success" />
                    ) : (
                      <IonIcon icon={radioButtonOffOutline} color="gray" />
                    )}
                  </button>
                );
              })}

              <button
                className="flex justify-between items-center gap-4 cursor-pointer disabled:opacity-50"
                disabled={isLoading}
                onClick={() => handleSelectPaymentMethod({ type: "ADD_NEW" })}
              >
                <div className="flex items-center gap-2">
                  <IonIcon icon={addOutline} className="text-[28px] mx-2" />
                  <div>{t("New Card")}</div>
                </div>
                {selectedPaymentMethod?.type === "ADD_NEW" ? (
                  <IonIcon icon={radioButtonOnOutline} color="success" />
                ) : (
                  <IonIcon icon={radioButtonOffOutline} color="gray" />
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </SpringBottomSheet>
  );
};

export default PaymentBottomSheet;
